import React, { useCallback } from 'react';
import { CronofyOptions } from '../../../../../components/cronofy/CronofyOptions';
import { InterviewStage } from '../../../../../entities/applicant_tracking/InterviewStage';
import { EventSlot } from '../../../../../components/cronofy/CronofyDatePicker/EventSlot';
import { InterviewService } from '../../../../../services/applicant_tracking/InterviewService';
import ScheduleInterviewPage from '../index';
import { User } from '../../../../../entities/User';
import { VcsProvider } from '../../../../../enums/IntegrationProvider/VcsProvider';
import { InterviewerParticipant } from '../../../../../entities/applicant_tracking/InterviewerParticipant';
import { appendCurrentRedirectUri } from '../../../../../utils/url';
import { addReturnTitle } from '../../../../../utils/applicant_tracking/actions/schedulingActions';
import { Candidate } from '../../../../../entities/applicant_tracking/Candidate';
import { QueryClientProvider } from 'react-query';
import { queryClient } from '../../../../../config/queryClient';
import { UpsertEmailTemplateDto } from '../../../../EmailTemplates/SetupEmailTemplatePage/UpsertEmailTemplateDto';
import { FeatureFlagContext } from '../../../../../contexts/FeatureFlagContext';

interface PropTypes {
  cronofyDatePickerOptions: CronofyOptions;
  applicationId: number;
  candidate: Candidate;
  currentUser: User;
  interviewStage: InterviewStage;
  meetingProviders: VcsProvider[];
  defaultMeetingProvider: VcsProvider;
  interviewTemplateId: number | null;
  personalizingSchedulingInvitesV1FfEnabled: boolean;
  personalizingSchedulingInvitesV2FfEnabled: boolean;
}

export default function ScheduleInterview(props: PropTypes) {
  const onConfirm = useCallback(
    async (
      eventSlot: EventSlot,
      participants: InterviewerParticipant[],
      meetingProvider: string,
      calendarEventTemplateId: number,
      calendarEventTemplateDto: UpsertEmailTemplateDto,
    ) => {
      try {
        const meetingOrganizer = participants.find(
          (p) => p.isMeetingOrganizer,
        ).user;
        const interview = await InterviewService.create(
          props.applicationId,
          props.interviewStage.id,
          participants.map((p) => p.user.id),
          eventSlot.slot.start,
          eventSlot.slot.end,
          eventSlot.tzid,
          meetingProvider,
          meetingOrganizer.id,
          calendarEventTemplateId,
          calendarEventTemplateDto,
        );
        const path = appendCurrentRedirectUri(
          `/applicant_tracking/interviews/${interview.id}/schedule_success`,
        );

        window.location.href = addReturnTitle(path);
      } catch (e: unknown) {
        window.location.href = appendCurrentRedirectUri(
          `/applicant_tracking/interviews/schedule_error?application_id=${props.applicationId}`,
        );
      }
    },
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <FeatureFlagContext.Provider
        value={{
          personalizingSchedulingInvitesV1FfEnabled:
            props.personalizingSchedulingInvitesV1FfEnabled,
          personalizingSchedulingInvitesV2FfEnabled:
            props.personalizingSchedulingInvitesV2FfEnabled,
        }}
      >
        <ScheduleInterviewPage
          meetingOrganizer={props.currentUser}
          applicationIds={[props.applicationId]}
          title='Schedule your interview'
          description='At least one participant needs to have the calendar connection'
          cronofyDatePickerOptions={props.cronofyDatePickerOptions}
          candidate={props.candidate}
          participants={[props.currentUser]}
          interviewStage={props.interviewStage}
          meetingProviders={props.meetingProviders}
          meetingProvider={props.defaultMeetingProvider}
          interviewTemplateId={props.interviewTemplateId}
          onConfirm={onConfirm}
          candidateView={false}
          selfScheduleView={false}
          allowChangingMeetingOrganizer={true}
          allowIgnoreParticipantCalendar={true}
          personalizingSchedulingInvitesV1FfEnabled={
            props.personalizingSchedulingInvitesV1FfEnabled
          }
        />
      </FeatureFlagContext.Provider>
    </QueryClientProvider>
  );
}
